import { ReactNode, useEffect } from 'react';
import { CareMapState } from './caremap/CareMapState';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { AlertPresenter } from './AlertBinder';
import { HistoryViewModel } from './support/HistoryViewModel';
import { AppPrincipal, isCaremapViewer } from './auth/AppPrincipal';
import { AnonymousPromoterState } from './auth/anonymous/AnonymousPromoterState';

export const BlockUntilActiveCaremap: React.FC<{children: ReactNode}> = ({ children }) => {
    const careMapState = useInstance(CareMapState);

    useSubscription(() => careMapState);

    return careMapState.hasActiveCareMap() ? children : null;
};

export const ActiveCareMapProviderV2: React.FC<{children: ReactNode; careMapId: string; }> = ({ children, careMapId }) => {
    const caremapState = useInstance(CareMapState);
    const alert = useInstance(AlertPresenter);
    const history = useInstance(HistoryViewModel);
    const isViewer = isCaremapViewer(useInstance(AppPrincipal));

    const promoterState = useInstance(AnonymousPromoterState);
    const isPromoting = !promoterState.state?.isComplete;

    useEffect(() => {
        Promise.resolve().then(async () => {
            if (caremapState.getActiveCaremap()?.careMapId !== careMapId){
                try {
                    await caremapState.setActiveCaremap(careMapId);
                }
                catch (err: any){
                    if (err && err.errorCode === 'not-found'){
                        if (isPromoting) {
                            return;
                        }
                        alert.showAlertV2({
                            header: 'Care Recipient Not Found',
                            message: `We're sorry, you no longer have access to the care recipient you are trying to view.`
                        });
                        caremapState.unsetCurrentCareMap();
                        history.push('/');
                    }
                }
            }

        });

        return () => {
            if (caremapState.getActiveCaremap()?.careMapId === careMapId){
                caremapState.unsetCurrentCareMap();
            }
        };
    }, [careMapId, caremapState, isPromoting, history, alert, isViewer]);

    if (caremapState.getActiveCaremap()?.careMapId === careMapId){
        return children;
    }
    return null;
};
